import { zpaNetworkAccessParameters } from "@p0security/integrations-zpa-shared/accesses";
import { startCase } from "lodash-es";

import { SshProviderKeys } from "../types.ts";

export const requiredProviderParameter = {
  description: "The cloud provider managing the instance",
  label: "Provider",
  type: "static",
  choices: SshProviderKeys,
  isPreselect: true,
} as const;

export const aliasParameter = {
  type: "generated",
  label: "Parent alias",
  description:
    "A user friendly name for the parent resource (e.g. an account alias)",
  optional: true,
} as const;

export const policyIdParameter = {
  type: "generated",
  label: "Policy ID",
  description: "The policy identifier (fingerprint)",
} as const;

export const policyNameParameter = {
  type: "generated",
  label: "Policy name",
  description: "The policy name",
  optional: true,
} as const;

export const sudoParameter = {
  type: "boolean",
  label: "Sudo",
  valueLabel: "Grant sudo access",
  optional: true,
} as const;

export const parentParameter = (label: string) =>
  ({
    type: "static",
    label: startCase(label),
    description: "The parent resource",
    optional: true,
  }) as const;

export const groupNameParameter = {
  description:
    "SSH group name (this is a unique value of the SSH group tag defined for your organization)",
  label: "Group name",
  type: "dynamic",
  optional: true,
} as const;

export const sessionDestinationParameter = {
  description: "Instance name or identifier",
  label: "Destination",
  type: "dynamic",
} as const;

export const regionParameter = {
  description: "The region in which the instance is located",
  label: "Region",
  type: "input",
  optional: true,
} as const;

export const zoneParameter = {
  type: "input",
  label: "Zone",
  optional: true,
} as const;

export const publicKeyParameter = {
  description:
    "Public key from the P0 CLI (contents of your ~/.p0/ssh/id_rsa.pub file).",
  hidden: true,
  hint: "To install the P0 CLI and generate the keypair, run npm -g install @p0security/cli and p0 ssh-keygen.",
  label: "Public key",
  type: "input",
  optional: true,
} as const;

export const groupDestinationParameter = {
  description:
    "Instance name or identifier; selects a group based on the destination's membership",
  label: "Destination",
  type: "dynamic",
  optional: true,
} as const;

// Used by both self-hosted and unknown Access definitions. Unknown needs it because it carries self-hosted's delegation transforms for Zscaler (legacy delegation doesn't recurse into self-hosted's lifecycle). See unknown.ts and self-hosted/session.ts for full context.
export const networkAccessParameter = {
  type: "object",
  label: "Network access",
  optional: true,
  properties: {
    zscalerApplicationSegment: {
      type: "object",
      label: "Zscaler application segment",
      optional: true,
      properties: zpaNetworkAccessParameters,
    },
    publicIp: {
      type: "generated",
      label: "Network IP",
      optional: true,
    },
  },
} as const;
